import { Box, Spinner, Center, Text } from '@chakra-ui/react';

import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { Message, TimelineItem } from '@/features/messages/messages.types';
import {
  ConversationUser,
  ConversationAgent,
  AgentType,
  ExtendedTimelineItem,
} from '../conversations.types';
import { useMessageState } from '../hooks/useMessageState';
import { useWhatsAppEvents } from '../hooks/useWhatsAppEvents';
import { groupMessagesByDate } from './conversation-message-utils';
import { DateGroupSection } from './conversation-date-group-section';
import { IoArrowDownCircleOutline } from 'react-icons/io5';

interface MessageThreadProps {
  conversationId: string;
  user: ConversationUser | null;
  aiAgent: ConversationAgent;
  humanAgent: ConversationAgent;
  activeAgentType: AgentType;
  isLoading?: boolean;
  timeline: TimelineItem[];
  pendingMessages?: Message[];
  channelId?: string | null;
  onRefreshMessages?: (message?: Message) => void;
  hasMore?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  hasPreviousPage?: boolean;
  isLoadScrollToBottomEnabled: boolean;
  setIsLoadScrollToBottomEnabled: (value: boolean) => void;
  onUpdateConversationToRead: () => void;
}

export function MessageThread({
  conversationId,
  user,
  aiAgent,
  humanAgent,
  isLoading = false,
  timeline = [],
  pendingMessages = [],
  channelId,
  onRefreshMessages,
  hasMore = false,
  fetchNextPage,
  isFetchingNextPage = false,
  setIsLoadScrollToBottomEnabled,
  isLoadScrollToBottomEnabled,
  onUpdateConversationToRead,
}: MessageThreadProps) {
  const messageEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [previousScrollHeight, setPreviousScrollHeight] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [isNewMessageArrived, setIsNewMessageArrived] = useState(false);
  const [lastMessage, setLastMessage] = useState<ExtendedTimelineItem | null>(
    null,
  );

  // Use custom hook for message state management
  const { combinedTimeline } = useMessageState({
    conversationId,
    timeline,
    initialPendingMessages: pendingMessages,
  });

  // Use custom hook for WhatsApp events
  useWhatsAppEvents({
    conversationId,
    channelId: channelId || null,
    onMessageReceived: onRefreshMessages,
  });

  const scrollToBottom = useCallback(() => {
    messageEndRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
    onUpdateConversationToRead();
  }, [onUpdateConversationToRead]);

  // Debug logging
  useEffect(() => {
    if (pendingMessages?.length) {
      console.log('Pending messages updated:', pendingMessages.length);
    }
    if (timeline?.length) {
      console.log(
        'Timeline updated, message count:',
        timeline.filter((item) => item.type === 'message').length,
      );
    }
  }, [pendingMessages, timeline]);

  // Handle scroll events for auto-loading and bottom detection
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10; // 10px threshold
    const isAtTop = scrollTop <= 50; // 50px threshold for loading more
    const nearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold for near bottom

    // Update near bottom state for floating button
    setIsNearBottom(nearBottom);

    // Track if user has scrolled to bottom (enables scroll-to-top loading)
    if (isAtBottom && !hasScrolledToBottom) {
      setHasScrolledToBottom(true);
    }

    if (isAtBottom) {
      setIsNewMessageArrived(false);
    }

    // Auto-load more messages when scrolling to top (only after scrolled to bottom once)
    if (isAtTop && hasScrolledToBottom && hasMore && !isFetchingNextPage) {
      fetchNextPage?.();
    }
  };

  // Handle scroll position preservation when loading more messages
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (isFetchingNextPage) {
      // Store current scroll height before loading more
      setPreviousScrollHeight(container.scrollHeight);
    } else if (previousScrollHeight > 0) {
      // After loading more messages, maintain scroll position
      const newScrollHeight = container.scrollHeight;
      const scrollDifference = newScrollHeight - previousScrollHeight;
      container.scrollTop = container.scrollTop + scrollDifference;
      setPreviousScrollHeight(0);
    }
  }, [isFetchingNextPage, combinedTimeline]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasScrolledToBottom, hasMore, isFetchingNextPage]);

  // Show scroll to bottom button when new messages arrive (not when loading more)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Check if user is near bottom before auto-scrolling
    const { scrollTop, scrollHeight, clientHeight } = container;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100; // 100px threshold

    const newLastMessage = combinedTimeline[combinedTimeline.length - 1];
    const senderId = (newLastMessage?.content as Message)?.sender_id;

    const isUserMessage = senderId === user?.id;
    if (
      !isNearBottom &&
      lastMessage?.id !== newLastMessage.id &&
      isLoadScrollToBottomEnabled
    ) {
      setIsNewMessageArrived(true);
      onUpdateConversationToRead();
      setLastMessage(newLastMessage);
      setIsLoadScrollToBottomEnabled(false);

      if (!isUserMessage) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }

    if (isLoadScrollToBottomEnabled && !isNearBottom) {
      setIsLoadScrollToBottomEnabled(false);
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [combinedTimeline, isFetchingNextPage]);

  // Reset states when conversation changes
  useEffect(() => {
    setIsLoadScrollToBottomEnabled(true);
    setHasScrolledToBottom(false);
    setIsNearBottom(true);
    setIsNewMessageArrived(false);
    setLastMessage(null);
  }, [conversationId]);

  // Group messages by date
  const dateGroups = useMemo(
    () => groupMessagesByDate(combinedTimeline),
    [combinedTimeline],
  );

  // Handle empty state
  if (dateGroups.length === 0 && !isLoading) {
    return (
      <Center height="100%" py={10}>
        <Text color="gray.500">No messages yet</Text>
      </Center>
    );
  }

  return (
    <Box
      ref={scrollContainerRef}
      height="100%"
      overflowY="auto"
      p={6}
      pb={32}
      position="relative"
    >
      {/* Loading indicator when fetching more messages */}
      {isFetchingNextPage && (
        <Box textAlign="center" py={4} mb={4}>
          <Spinner size="md" color="blue.500" />
          <Text fontSize="sm" color="gray.500" mt={2}>
            {hasScrolledToBottom ? 'Loading older messages...' : 'Loading...'}
          </Text>
        </Box>
      )}

      {/* Scroll to load more hint */}
      {hasScrolledToBottom && hasMore && !isFetchingNextPage && (
        <Box textAlign="center" py={2} mb={2}>
          <Text fontSize="xs" color="gray.400">
            Scroll to top to load older messages
          </Text>
        </Box>
      )}

      {dateGroups.map((group, groupIndex) => (
        <DateGroupSection
          key={`group-${group.date}-${groupIndex}`}
          group={group}
          user={user}
          aiAgent={aiAgent}
          humanAgent={humanAgent}
        />
      ))}

      {/* Floating scroll to bottom button */}
      {!isNearBottom && (
        <Box
          position="sticky"
          bottom="0"
          w="fit-content"
          ml="auto"
          zIndex={1000}
          bg="white"
          onClick={scrollToBottom}
          cursor="pointer"
          borderRadius="full"
          p={1}
          transition="all 0.2s"
          _hover={{
            transform: 'scale(1.05)',
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
          }}
          _active={{
            transform: 'scale(0.95)',
          }}
        >
          {/* Arrow down icon */}
          <IoArrowDownCircleOutline size={25} />

          {isNewMessageArrived && (
            <Box
              position="absolute"
              top="0"
              right="0"
              width="10px"
              height="10px"
              borderRadius="full"
              bg="cyan.500"
            />
          )}
        </Box>
      )}

      {/* Invisible element at the bottom to scroll to */}
      <div ref={messageEndRef} />
    </Box>
  );
}
